import DomainModel from "../domain-model";
import logging from "../logging";

const log = logging('cache');

const DEBUG = true;

const pickProperties = (model: DomainModel, props: string[]) => {
    const map = {};
    for(const prop of props) {
        if(DEBUG) {
            if(!model.hasField(prop)) {
                log.error(`Domain model has no field named "${prop}"`, model);
            }
        }
        let value = model.get(prop);
        if(value instanceof DomainModel) {
            value = value.toStateObject();
        } else if(Array.isArray(value)
                  && value.length > 0
              && value[0] instanceof DomainModel)
        {
            value = value.map(model => model.toStateObject())
        }
        map[prop] = value;
    }
    return map;
}

/**
 * The CacheByModelValue class caches objects in memory based on a DomainModel
 * key. The domain models don't all have to be of the same type, but the
 * objects contained in the cache should be.
 *
 * The model should also be relatively shallow, or this isn't going to work
 * well. We want to be able to discriminate any change that impacts the
 * geometry but none that might change freely, such as position.
 */
export default class CacheByModelValue<T> {

    cache: Map<string, T> = new Map();

    modelProperties: string[];

    constructor(modelProperties: string[]) {
        this.modelProperties = modelProperties;
    }

    put(model: DomainModel, item: T) {
        const key = this.keyOf(model);
        this.cache.set(key, item);
    }

    get(model: DomainModel) {
        const key = this.keyOf(model);
        if(this.cache.has(key)){
            return this.cache.get(key);
        } else {
            return null;
        }
    }

    has(model: DomainModel): boolean {
        const key = this.keyOf(model);
        return this.cache.has(key);
    }

    private keyOf(model: DomainModel) {
        return JSON.stringify(pickProperties(model, this.modelProperties))
    }

    /**
     * Note that this memoize implementation is limited in the same way that
     * put and get are: the arguments to fn should be unique when turned into a
     * string.
     *
     * The additional arguments passed in the `...args` array are for static
     * global parameters not expected to change for any instance during the
     * user's session.
     */
    static memoize<T>(modelProperties: string[],
                      fn: (model: DomainModel, ...args: unknown[]) => T)
    {
        const cache: CacheByModelValue<T> = new this(modelProperties);
        const wrapper = (model: DomainModel, ...args: unknown[]) => {
            var result = cache.get(model);
            if(result) {
                return result;
            } else {
                result = fn(model, ...args);
                cache.put(model, result);
                return result;
            }
        }
        return wrapper;
    }
}
